import { useState, useEffect, createContext, useContext, ReactNode, useCallback, useMemo, useRef } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  subscribed: boolean;
  subscriptionEnd: string | null;
  subscriptionChecked: boolean;
  checkSubscription: (session?: Session | null) => Promise<boolean>;
  signOut: () => Promise<void>;
  isInTrial: boolean;
  trialDaysLeft: number;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const userIdRef = useRef<string | null>(null); // Track user ID to detect actual user changes
  const [loading, setLoading] = useState(true);
  const [subscribed, setSubscribed] = useState(false);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [isInTrial, setIsInTrial] = useState(false);
  const [trialDaysLeft, setTrialDaysLeft] = useState(0);
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const navigate = useNavigate();

  const checkSubscription = useCallback(async (currentSession: Session | null = session, isInitialCheck = false): Promise<boolean> => {
    if (!currentSession) return false;

    try {
      // Only set subscriptionChecked to false on initial check
      // This prevents unmounting protected components during periodic refreshes
      if (isInitialCheck) {
        setSubscriptionChecked(false);
      }

      const { data, error } = await supabase.functions.invoke("check-subscription", {
        headers: {
          Authorization: `Bearer ${currentSession.access_token}`,
        },
      });

      if (error) throw error;

      setSubscribed(data.subscribed);
      setSubscriptionEnd(data.subscription_end);
      // Backend now calculates trial status
      setIsInTrial(data.is_in_trial ?? false);
      setTrialDaysLeft(data.trial_days_left ?? 0);
      setSubscriptionChecked(true);
      return data.subscribed;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error checking subscription:", error);
      }
      // On error, keep the previous state instead of resetting to false
      // This prevents UI flickering if the API call fails intermittently
      setSubscriptionChecked(true);
      return subscribed; // Return current state
    }
  }, [session, subscribed]);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (event === "PASSWORD_RECOVERY") {
          navigate("/reset-password");
        }

        if (newSession) {
          // Use the session from the event, not the state
          setTimeout(() => {
            // Only force a blocking check (loader) for login or initial session IF it's a new user
            // This prevents "refreshing" when switching tabs (which might fire SIGNED_IN)
            const currentUserId = newSession.user.id;
            const isNewUser = userIdRef.current !== currentUserId;

            // Update ref
            userIdRef.current = currentUserId;

            const isLoginEvent = (event === "SIGNED_IN" || event === "INITIAL_SESSION") && isNewUser;
            checkSubscription(newSession, isLoginEvent);
          }, 0);
        } else {
          setSubscribed(false);
          setSubscriptionEnd(null);
          setSubscriptionChecked(false);
        }

        setLoading(false);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      setSession(existingSession);
      setUser(existingSession?.user ?? null);

      if (existingSession) {
        setTimeout(() => {
          checkSubscription(existingSession, true);
        }, 0);
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to prevent infinite loops

  // Auto-refresh subscription every minute
  useEffect(() => {
    if (!session) return;

    const interval = setInterval(() => {
      checkSubscription(session);
    }, 60000); // 1 minute

    return () => clearInterval(interval);
  }, [session, checkSubscription]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  }, [navigate]);

  const value = useMemo(() => ({
    user,
    session,
    loading,
    subscribed,
    subscriptionEnd,
    subscriptionChecked,
    checkSubscription,
    signOut,
    isInTrial,
    trialDaysLeft,
  }), [
    user,
    session,
    loading,
    subscribed,
    subscriptionEnd,
    subscriptionChecked,
    checkSubscription,
    signOut,
    isInTrial,
    trialDaysLeft,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
